import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  Cog6ToothIcon,
  ShareIcon,
  // EllipsisVerticalIcon, // Unused - removed
  // PlusIcon, // Unused - removed
  ArrowPathIcon,
  PlayIcon,
  PauseIcon
} from '@heroicons/react/24/outline';

// Import widget components
import { CircularGauge, LinearGauge } from './widgets/GaugeWidgets';
// import BaseCard from './cards/BaseCard'; // Unused './cards/BaseCard';
import WidgetWrapper from './WidgetWrapper';
import DataSourceManager from './DataSourceManager';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 2rem;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

const DashboardInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const DashboardTitle = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.h1};
  margin: 0;
`;

const DashboardSubtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.md};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary.main};
    color: white;
    
    &:hover {
      background: ${theme.colors.primary.dark};
    }
  ` : `
    background: ${theme.colors.background.paper};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.secondary};
    
    &:hover {
      background: ${theme.colors.background.secondary};
    }
  `}
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatusBar = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const StatusDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'online': return theme.colors.success.main;
      case 'warning': return theme.colors.warning.main;
      case 'offline': return theme.colors.error.main;
      default: return theme.colors.text.secondary;
    }
  }};
  animation: ${props => props.status === 'online' ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: ${theme.colors.text.secondary};
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${theme.colors.error.main};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${theme.colors.text.secondary};
`;

const ModernDashboard = ({ dashboardId, dashboardData }) => {
  const { id: paramId } = useParams();
  const id = dashboardId || paramId;
  const navigate = useNavigate();
  // const { user } = useAuth(); // Unused
  const [dashboard, setDashboard] = useState(dashboardData || null);
  const [dashboardWidgetData, setDashboardWidgetData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    if (id) {
      if (!dashboard) {
        loadDashboard();
      }
      loadDashboardData();
      startAutoRefresh();
    } else {
      // If no ID, redirect to dashboard manager
      navigate('/dashboards');
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [id, dashboard]);

  const startAutoRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    
    const interval = setInterval(() => {
      if (isAutoRefresh) {
        loadDashboardData(true); // Silent refresh of data
      }
    }, 30000); // 30 seconds
    
    setRefreshInterval(interval);
  };

  const loadDashboard = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get(`/api/dashboards/${id}`);
      setDashboard(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard');
      if (!silent) {
        toast.error('Failed to load dashboard');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadDashboardData = async (silent = false) => {
    try {
      if (!dashboard) return;
      
      const response = await api.get(`/api/dashboards/${id}/data`);
      setDashboardWidgetData(response.data);
      setLastUpdate(new Date());
      
      if (!silent) {
        console.log(`📊 Loaded data for ${Object.keys(response.data).length} widget paths`);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      if (!silent) {
        toast.error('Failed to load dashboard data');
      }
    }
  };

  const toggleAutoRefresh = () => {
    setIsAutoRefresh(!isAutoRefresh);
    if (!isAutoRefresh) {
      startAutoRefresh();
    }
  };

  const handleManualRefresh = () => {
    loadDashboard();
    loadDashboardData();
    toast.success('Dashboard refreshed');
  };

  const handleBack = () => {
    navigate('/dashboards');
  };

  const handleEdit = () => {
    navigate(`/dashboard/${id}/edit`);
  };

  const handleShare = async () => {
    try {
      const response = await api.post(`/api/dashboards/${id}/share`);
      const shareUrl = `${window.location.origin}/dashboard/shared/${response.data.sharing_token}`;
      
      if (navigator.share) {
        await navigator.share({
          title: dashboard.dashboard_name,
          text: `Check out my vessel dashboard: ${dashboard.dashboard_name}`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing dashboard:', err);
      toast.error('Failed to share dashboard');
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingState>
          <div>Loading dashboard...</div>
        </LoadingState>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <ErrorState>
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </ErrorState>
      </DashboardContainer>
    );
  }

  if (!dashboard) {
    return (
      <DashboardContainer>
        <EmptyState>
          <h2>Dashboard Not Found</h2>
          <p>The dashboard you're looking for doesn't exist or you don't have access to it.</p>
          <Button onClick={handleBack}>
            Back to Dashboards
          </Button>
        </EmptyState>
      </DashboardContainer>
    );
  }

  const getDashboardStatus = () => {
    if (!lastUpdate) return 'offline';
    
    const now = new Date();
    const diff = now - lastUpdate;
    const minutes = diff / (1000 * 60);
    
    if (minutes < 5) return 'online';
    if (minutes < 30) return 'warning';
    return 'offline';
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Live Data';
      case 'warning': return 'Inactive Data';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return 'Never';
    return `Last updated: ${timestamp.toLocaleTimeString()}`;
  };

  const renderWidget = (widget) => {
    // Handle both camelCase and snake_case data path properties
    const dataPath = widget.dataPath || widget.data_path;
    const widgetData = dashboardWidgetData[dataPath];
    const value = widgetData?.value;
    const unit = widgetData?.unit || '';
    
    // Log data issues for debugging
    if (!widgetData) {
      console.log(`No data found for widget "${widget.name}" at path "${dataPath}"`);
    }
    
    // Parse config if it's a string
    let config = {};
    try {
      config = typeof widget.config === 'string' ? JSON.parse(widget.config) : (widget.config || {});
    } catch (e) {
      console.warn('Failed to parse widget config:', widget.config);
    }

    switch (widget.type) {
      case 'gauge':
        return (
          <CircularGauge
            name={widget.name}
            value={value}
            unit={unit}
            min={config.min || 0}
            max={config.max || 100}
            config={config}
          />
        );
      
      case 'digital':
        return (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            padding: '1rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: theme.colors.text.primary, fontSize: '0.875rem' }}>
              {widget.name}
            </h4>
            <div style={{ color: theme.colors.primary.main, fontSize: '2rem', fontWeight: 'bold' }}>
              {value !== undefined ? value : '--'}
            </div>
            <div style={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
              {unit}
            </div>
          </div>
        );
      
      case 'progress':
        return (
          <LinearGauge
            name={widget.name}
            value={value}
            unit={unit}
            min={config.min || 0}
            max={config.max || 100}
            config={config}
          />
        );
      
      case 'compass':
        return (
          <CircularGauge
            name={widget.name}
            value={value}
            unit={unit || '°'}
            min={0}
            max={360}
            config={{ ...config, type: 'compass' }}
          />
        );
      
      case 'map':
        return (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            padding: '1rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: theme.colors.text.primary }}>
              {widget.name}
            </h4>
            <div style={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
              {value ? `${value.latitude?.toFixed(4)}, ${value.longitude?.toFixed(4)}` : 'No position data'}
            </div>
          </div>
        );
      
      default:
        return (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            padding: '1rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: theme.colors.text.primary }}>
              {widget.name}
            </h4>
            <div style={{ color: theme.colors.primary.main, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {value || '--'}
            </div>
            <div style={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
              {unit}
            </div>
          </div>
        );
    }
  };

  return (
    <DashboardContainer>
      <DashboardHeader>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </BackButton>
          <DashboardInfo>
            <DashboardTitle>{dashboard.dashboard_name}</DashboardTitle>
            <DashboardSubtitle>
              {dashboard.vessel_name || 'No vessel assigned'} • {dashboard.dashboard_type}
            </DashboardSubtitle>
          </DashboardInfo>
        </HeaderLeft>
        <HeaderActions>
          <Button onClick={handleManualRefresh} title="Refresh Dashboard">
            <ArrowPathIcon className="w-4 h-4" />
          </Button>
          <Button onClick={toggleAutoRefresh} title={isAutoRefresh ? 'Pause Auto-refresh' : 'Start Auto-refresh'}>
            {isAutoRefresh ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
          </Button>
          <Button onClick={handleShare}>
            <ShareIcon className="w-4 h-4" />
            Share
          </Button>
          <Button onClick={handleEdit}>
            <Cog6ToothIcon className="w-4 h-4" />
            Edit
          </Button>
        </HeaderActions>
      </DashboardHeader>

      <StatusBar>
        <StatusItem>
          <StatusDot status={getDashboardStatus()} />
          {getStatusText(getDashboardStatus())}
        </StatusItem>
        <StatusItem>
          {formatLastUpdate(lastUpdate)}
        </StatusItem>
        <StatusItem>
          Vessel: {dashboard.vessel_name || 'Not assigned'}
        </StatusItem>
        <StatusItem>
          Update Frequency: {dashboard.update_frequency || 60}s
        </StatusItem>
        <StatusItem>
          <DataSourceManager
            vesselId={dashboard.vessel_id}
            onSourceChange={(newSource) => {
              console.log('Dashboard data source changed to:', newSource);
              toast.success(`Switched to ${newSource} data source`);
              // Reload dashboard data with new source
              loadDashboardData();
            }}
          />
        </StatusItem>
      </StatusBar>

      {/* Render sections and widgets dynamically */}
      {dashboard.sections && dashboard.sections.map((section) => (
        <div key={section.id} style={{ marginBottom: '2rem' }}>
          <h2 style={{ 
            color: theme.colors.text.primary, 
            fontSize: theme.fontSizes.lg,
            marginBottom: '1rem',
            fontWeight: theme.fontWeights.semibold
          }}>
            {section.name}
          </h2>
          
          <DashboardGrid>
            {section.widgets && section.widgets.map((widget) => (
              <WidgetWrapper
                key={widget.id}
                widget={widget}
                showControls={true}
                showDataSource={true}
                lastUpdate={dashboardWidgetData[widget.dataPath || widget.data_path]?.timestamp}
                isOffline={!dashboardWidgetData[widget.dataPath || widget.data_path]}
                onEdit={(w) => navigate(`/dashboard/${id}/edit`)}
              >
                {renderWidget(widget)}
              </WidgetWrapper>
            ))}
          </DashboardGrid>
        </div>
      ))}
    </DashboardContainer>
  );
};

export default ModernDashboard;